import { FC } from "react";

import { UserStateStatsType } from "../../shared/types/monitoring";
import { useAppSelector } from "../../store/index";
import { selectIsClassSessionVirtual } from "../../store/selectors/class-session";
import { selectUserByClassUserId } from "../../store/selectors/class-user";
import {
  selectCameraErrorStatsBySectionId,
  selectHighPingStatsBySectionId,
  selectMicErrorStatsBySectionId,
  selectPoorConnectionStatsBySectionId,
  selectPubsubConnectionStatsBySectionId,
} from "../../store/selectors/monitoring";
import { selectOnlineUserCountInCurrentSection } from "../../store/selectors/section";
import { selectEnabledSectionUsersForCurrentSection } from "../../store/selectors/section-user";
import { useCountSelector, useObjectSelector } from "../../store/store-hooks";
import { PROFILE_PROPERTIES, getUserFullName } from "../../utils/user-utils";

import {
  AvRefreshStatsTD,
  FailedApiRequestsTD,
  PageLoadStatsTD,
  PageWorksheetLoadErrorTD,
  PoorConnectionWarningTD,
  PreclassCheckFailStatsTD,
  ReloadStreamStatsTD,
  TD,
  TDStatusIconWithTooltip,
  WebsocketDisconnectionsTD,
} from "./Table";

import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { selectCurrentSectionId } from "store/selectors";

const SectionStatsHeader: FC = () => {
  const isVirtualClass = useAppSelector(selectIsClassSessionVirtual);
  return (
    <thead>
      <tr>
        <th className="!pl-0">Attendance</th>
        <th>
          <TooltipTrigger content="Number of page loads per participant">Page Loads</TooltipTrigger>
        </th>
        <th>
          <TooltipTrigger content="Number of websocket disconnections per participant">
            Websocket Disconnections
          </TooltipTrigger>
        </th>
        <th>
          <TooltipTrigger content="Number of failed API requests per participant">
            Failed API Requests
          </TooltipTrigger>
        </th>
        <th>
          <TooltipTrigger content="Number of page load errors per participant">
            Page Load Errors
          </TooltipTrigger>
        </th>
        <th>
          <TooltipTrigger content="Number of poor connection warnings per participant">
            Poor Connection Warnings
          </TooltipTrigger>
        </th>
        {isVirtualClass && (
          <>
            <th>
              <TooltipTrigger content="Number of AV (excluding av-service-requested and update-service-info) refreshes per participant and percentage of users affected">
                AV Refreshes
              </TooltipTrigger>
            </th>
            <th>
              <TooltipTrigger content="Number of stream reloads per participant and percentage of users affected">
                Stream Reloads
              </TooltipTrigger>
            </th>
          </>
        )}
        <th>
          <TooltipTrigger content="Number of users who are not connected to the Pubsub server">
            Pubsub Connections
          </TooltipTrigger>
        </th>
        <th>
          <TooltipTrigger content="Number of users with high ping round trip time to the Pubsub server">
            Pubsub Ping
          </TooltipTrigger>
        </th>
        {isVirtualClass && (
          <>
            <th>
              <TooltipTrigger content="Number of users with mic errors">Mic Errors</TooltipTrigger>
            </th>
            <th>
              <TooltipTrigger content="Number of users with camera errors">
                Camera Errors
              </TooltipTrigger>
            </th>
            <th>
              <TooltipTrigger content="Number of users with poor connection quality to the A/V server">
                A/V Connections
              </TooltipTrigger>
            </th>
            <th>
              <TooltipTrigger content="Number of Test failures per participant and percentage of users affected">
                Pre-class Check Failures
              </TooltipTrigger>
            </th>
          </>
        )}
      </tr>
    </thead>
  );
};

const SectionStatsTooltipContentUser: FC<{
  classUserId: string;
}> = ({ classUserId }) => {
  const user = useObjectSelector(
    (state) => selectUserByClassUserId(state, classUserId),
    PROFILE_PROPERTIES,
  );
  return (
    <div className="text-left">{user ? getUserFullName(user) : `Class user ${classUserId}`}</div>
  );
};

const SectionStatsTooltipContent: FC<{
  userStateStats: UserStateStatsType;
  keyPrefix: string;
}> = ({ userStateStats, keyPrefix }) => {
  return (
    <>
      {userStateStats?.percentAffectedUsers === 0 ? (
        "0% of users affected"
      ) : (
        <>
          <div className="mb-1 text-left font-bold">{`${userStateStats.formattedPercentage} of users affected:`}</div>
          {userStateStats.affectedUsers.map((classUserId) => {
            return (
              <SectionStatsTooltipContentUser
                classUserId={classUserId}
                key={`${keyPrefix}-${classUserId}`}
              />
            );
          })}
        </>
      )}{" "}
    </>
  );
};

const SectionStatsRow: FC<{ sectionId: string }> = ({ sectionId }) => {
  // TODO: get avService, licodeServer, nActiveUsers and nTotalUsers corresponding to the given
  // sectionId instead of the current section.
  const isVirtualClass = useAppSelector(selectIsClassSessionVirtual);

  const nActiveUsers = useAppSelector(selectOnlineUserCountInCurrentSection);
  const nTotalUsers = useCountSelector(selectEnabledSectionUsersForCurrentSection);

  const micErrorStats = useAppSelector((state) => selectMicErrorStatsBySectionId(state, sectionId));
  const cameraErrorStats = useAppSelector((state) =>
    selectCameraErrorStatsBySectionId(state, sectionId),
  );
  const highPingStats = useAppSelector((state) => selectHighPingStatsBySectionId(state, sectionId));
  const poorConnectionStats = useAppSelector((state) =>
    selectPoorConnectionStatsBySectionId(state, sectionId),
  );
  const pubsubConnectionStats = useAppSelector((state) =>
    selectPubsubConnectionStatsBySectionId(state, sectionId),
  );
  return (
    <tr>
      <TD dataTestId="attendance">{`${nActiveUsers}/${nTotalUsers}`}</TD>
      {/* <TD
          dataTestId="bug-report-count"
          conditionForDanger={bugReportCount >= 1}
        >{`${bugReportCount}`}</TD> */}
      <PageLoadStatsTD sectionId={sectionId} />
      <WebsocketDisconnectionsTD sectionId={sectionId} />
      <FailedApiRequestsTD sectionId={sectionId} />
      <PageWorksheetLoadErrorTD sectionId={sectionId} />
      <PoorConnectionWarningTD sectionId={sectionId} />
      {isVirtualClass && (
        <>
          <AvRefreshStatsTD sectionId={sectionId} />
          <ReloadStreamStatsTD sectionId={sectionId} />
        </>
      )}
      <TDStatusIconWithTooltip
        conditionForDanger={
          !!pubsubConnectionStats?.percentAffectedUsers &&
          pubsubConnectionStats.percentAffectedUsers >= 50
        }
        conditionForWarning={
          !!pubsubConnectionStats?.percentAffectedUsers &&
          pubsubConnectionStats.percentAffectedUsers >= 20
        }
        dataTestId="pubsub-connection"
      >
        <SectionStatsTooltipContent
          keyPrefix="pubsub-connection"
          userStateStats={pubsubConnectionStats}
        />
      </TDStatusIconWithTooltip>
      <TDStatusIconWithTooltip
        conditionForDanger={
          !!highPingStats?.percentAffectedUsers && highPingStats.percentAffectedUsers >= 50
        }
        conditionForWarning={
          !!highPingStats?.percentAffectedUsers && highPingStats.percentAffectedUsers >= 20
        }
        dataTestId="high-ping"
      >
        <SectionStatsTooltipContent keyPrefix="high-ping" userStateStats={highPingStats} />
      </TDStatusIconWithTooltip>
      {isVirtualClass && (
        <>
          <TDStatusIconWithTooltip
            conditionForDanger={
              !!micErrorStats?.percentAffectedUsers && micErrorStats.percentAffectedUsers >= 50
            }
            conditionForWarning={
              !!micErrorStats?.percentAffectedUsers && micErrorStats.percentAffectedUsers >= 20
            }
            dataTestId="mic-error"
          >
            <SectionStatsTooltipContent keyPrefix="mic-error" userStateStats={micErrorStats} />
          </TDStatusIconWithTooltip>
          <TDStatusIconWithTooltip
            conditionForDanger={
              !!cameraErrorStats?.percentAffectedUsers &&
              cameraErrorStats.percentAffectedUsers >= 50
            }
            conditionForWarning={
              !!cameraErrorStats?.percentAffectedUsers &&
              cameraErrorStats.percentAffectedUsers >= 20
            }
            dataTestId="camera-error"
          >
            <SectionStatsTooltipContent
              keyPrefix="camera-error"
              userStateStats={cameraErrorStats}
            />
          </TDStatusIconWithTooltip>
          <TDStatusIconWithTooltip
            conditionForDanger={
              !!poorConnectionStats?.percentAffectedUsers &&
              poorConnectionStats.percentAffectedUsers >= 50
            }
            conditionForWarning={
              !!poorConnectionStats?.percentAffectedUsers &&
              poorConnectionStats.percentAffectedUsers > 20
            }
            dataTestId="poor-connection"
          >
            <SectionStatsTooltipContent
              keyPrefix="poor-connection"
              userStateStats={poorConnectionStats}
            />
          </TDStatusIconWithTooltip>
          <PreclassCheckFailStatsTD sectionId={sectionId} />
        </>
      )}
    </tr>
  );
};

export const SectionStats: FC = () => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  if (!sectionId) return null;
  return (
    <table className="mds-table table-hover" data-testid="section-stats-table">
      <SectionStatsHeader />
      <tbody>
        <SectionStatsRow sectionId={sectionId} />
      </tbody>
    </table>
  );
};
